/**
 * ws-client.ts — Socket.IO client factory for the Maloum CRM gateway (chatter surface).
 *
 * Security (T-04-33 / T-04-37):
 *   - Connects to OUR NestJS gateway only (not wss://ws.maloum.com)
 *   - Authenticated via the httpOnly session cookie (withCredentials: true)
 *   - Server scopes events to assigned accounts only — client cannot widen (D-08)
 *
 * Typed subscriptions for @crm/types/socket events:
 *   - health:update         → HealthUpdate          (per-account health FSM)
 *   - inbox:delta           → InboxDelta            (new/changed conversations — scoped)
 *   - conversation:claimed  → ConversationClaimEvent (passive collision indicator — D-05-18)
 */

import { io, type Socket } from 'socket.io-client';
import type { HealthUpdate, InboxDelta } from '@crm/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type UnsubFn = () => void;

/**
 * Payload of the `conversation:claimed` WS event (D-05-18, passive collision chip).
 * Emitted by the server to OTHER chatters assigned to the account when a chatter
 * opens/claims a conversation. Mirrors the server's ConversationClaimDto.
 */
export interface ConversationClaimEvent {
  /** CRM conversation UUID. */
  id: string;
  /** The chatter who now holds the (passive) claim; null clears it. */
  claimed_by_chatter_id: string | null;
  /** ISO timestamp of the claim. */
  claimed_at: string | null;
}

export interface WsClient {
  /** Subscribe to per-account health FSM updates (D-18). */
  onHealthUpdate: (handler: (evt: HealthUpdate) => void) => UnsubFn;
  /** Subscribe to inbox delta events (scoped to assigned accounts — D-08). */
  onInboxDelta: (handler: (evt: InboxDelta) => void) => UnsubFn;
  /**
   * Subscribe to passive collision-claim events (D-05-18). Fires when another
   * chatter opens/claims a conversation the viewer can see; drives CollisionChip.
   */
  onConversationClaim: (handler: (evt: ConversationClaimEvent) => void) => UnsubFn;
  /** Disconnect and clean up. */
  disconnect: () => void;
  /** The underlying socket (for advanced use). */
  socket: Socket;
}

// ---------------------------------------------------------------------------
// Singleton socket (one connection per chatter session)
// ---------------------------------------------------------------------------

let _socket: Socket | null = null;

function getSocket(): Socket {
  if (_socket && _socket.connected) return _socket;
  // Connect to the NestJS gateway (same origin in production; proxied in dev)
  _socket = io('/', {
    path: '/socket.io',
    // httpOnly cookie is sent automatically by the browser via withCredentials
    withCredentials: true,
    transports: ['websocket', 'polling'],
    reconnectionDelay: 2000,
    reconnectionDelayMax: 30000,
    reconnectionAttempts: Infinity,
    timeout: 20000,
  });
  return _socket;
}

// ---------------------------------------------------------------------------
// WsClient factory
// ---------------------------------------------------------------------------

export function createWsClient(): WsClient {
  const socket = getSocket();

  function on<T>(event: string, handler: (evt: T) => void): UnsubFn {
    socket.on(event, handler as (...args: unknown[]) => void);
    return () => socket.off(event, handler as (...args: unknown[]) => void);
  }

  return {
    socket,
    onHealthUpdate: (handler) => on<HealthUpdate>('health:update', handler),
    onInboxDelta: (handler) => on<InboxDelta>('inbox:delta', handler),
    onConversationClaim: (handler) =>
      on<ConversationClaimEvent>('conversation:claimed', handler),
    disconnect: () => {
      socket.disconnect();
      _socket = null;
    },
  };
}
